import React from "react";
import { Button, Title } from "rbx";
import PropTypes from "prop-types";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import {
  UPDATE_ONE_USER_FOLLOWUP,
  GET_USER,
  DELETE_ONE_USER_FOLLOWUP
} from "../../graphql";

import { Loader } from "../../../../components";
import formatDate from "../../../../utils/formatDate";
import { useAuth } from "../../../../context";
import capitalizeEveryWord from "../../../../utils/capitalizeEveryWord";

const FollowupForm = ({
  currentFollowupId,
  employeeNumber,
  followup,
  followups,
  loading,
  permissions,
  onComplete,
  refetchMyFollowups,
  onCanceled,
  isCompletedSection = false,
  type,
}) => {

  const auth = useAuth();

  const activeUserEmployeeNumber = auth?.state?.user?.employeeNumber;
  const activeUserFullName = `${auth?.state?.user?.firstName} ${auth?.state?.user?.lastName}`;

  // eslint-disable-next-line no-unused-vars
  const [updateOneUserFollowup, { loading: loadingFollowup }] = useMutation(
    UPDATE_ONE_USER_FOLLOWUP
  );

  const [deleteOneUserFollowup, { loading: loadingDeleteFollowup }] = useMutation(
    DELETE_ONE_USER_FOLLOWUP
  );

  // eslint-disable-next-line no-unused-vars
  const { data: UserData, loadingUserData } = useQuery(GET_USER, {
    variables: {
      where: {
        employeeNumber,
      },
    },
  });

  const handleFollowupCompletion = async (e) => {
    try {
      e.preventDefault();

      const timeStamp = new Date().toISOString();
      await updateOneUserFollowup({
        variables: {
          where: {
            id: currentFollowupId,
          },
          data: {
            status: {
              set: "COMPLETED",
            },
            updatedBy: {
              set: activeUserEmployeeNumber,
            },
            updatedByFullName: {
              set: activeUserFullName,
            },
            updatedAt: {
              set: timeStamp,
            }
          },
        },
      });

      await setTimeout(1000);

      toast.success("Successfully updated followup");
      onComplete();
    } catch (err) {
      toast.error("Error saving followup, please contact support.");
    }
  };

  const handleDeleteFollowup = async (e) => {
    try {
      e.preventDefault();

      // Update to remove from lists first
      await updateOneUserFollowup({
        variables: {
          where: {
            id: currentFollowupId,
          },
          data: {
            status: {
              set: "TO_BE_DELETED",
            },
          }
        }
      });

      await deleteOneUserFollowup({
        variables: {
          where: {
            id: currentFollowupId,
          },
        },
      });

      toast.success("Successfully deleted followup");
      onComplete();
    } catch (err) {
      toast.error("Error deleting followup, please contact support.");
    }
  };

  if (loading || loadingUserData || loadingDeleteFollowup) {
    return (
      <div style={{ minHeight: "10rem" }}>
        <Loader />
      </div>
    );
  }

  return (
    <form id="followup-form" onSubmit={handleFollowupCompletion}>
      <div className="evaluation-container">
        <header className="modal-head" style={{}}>
          <div className="modal-head">
            <Title size={5}>{followup.title}</Title>
          </div>
          <div className="modal-head">
            <Button.Group hasAddons>
              <Button
                size="small"
                style={{ marginBottom: "0.5rem" }}
                onClick={onCanceled}
              >
                <span>Cancel</span>
              </Button>
              {!isCompletedSection && activeUserEmployeeNumber.trim() !== employeeNumber.trim() && type === "delete" && (
                <React.Fragment>
                  <Button
                    color="danger"
                    size="small"
                    style={{ marginBottom: "0.5rem", marginLeft: "0.5rem", marginRight: "0.2rem" }}
                    onClick={handleDeleteFollowup}
                  >
                    <span>Delete</span>
                  </Button>
                  <Button
                    color="primary"
                    form="followup-form"
                    size="small"
                    style={{ marginBottom: "0.5rem" }}
                    type="submit"
                  >
                    <span>Complete</span>
                  </Button>
                </React.Fragment>
              )}
            </Button.Group>
          </div>
        </header>
        <div className="modal-head-details">
          <p><b>Status:</b> {capitalizeEveryWord(followup?.status)}</p>
          <p><b>Created By:</b>{` ${capitalizeEveryWord(followup?.createdBy)}`}</p>
          <p><b>Create Date:</b> {formatDate(followup?.createDate)}</p>
          {followup?.followupDate && (
            <p><b>Follow-up Date:</b> {formatDate(followup?.followupDate)}</p>
          )}
          {followup?.assignedToUser && (
            <p><b>Assigned To:</b>{` ${capitalizeEveryWord(followup?.assignedToUser?.firstName)} ${capitalizeEveryWord(followup?.assignedToUser?.lastName)}`}</p>
          )}
          <p><b>Updated By:</b>{` ${capitalizeEveryWord(followup?.updatedByFullName)}`}</p>
          <p><b>Updated Date:</b>{` ${formatDate(followup?.updatedAt)}`}</p>
        </div>
        <hr />
      </div>

      <p>
        <b>Description:</b> {` ${followup?.description}`}
      </p>
    </form>
  );
};

FollowupForm.propTypes = {
  loading: PropTypes.bool.isRequired,
  employeeNumber: PropTypes.string.isRequired,
  followup: PropTypes.object.isRequired,
  followups: PropTypes.array.isRequired,
  permissions: PropTypes.array.isRequired,
  currentFollowupId: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
  refetchMyFollowups: PropTypes.func.isRequired,
  onCanceled: PropTypes.func.isRequired,
  isCompletedSection: PropTypes.bool,
  type: PropTypes.string,
};

FollowupForm.defaultProps = {
  isCompletedSection: false,
  type: "view",
};

export default FollowupForm; 